import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../lib/utils';
import { 
  Save, RefreshCw, CheckCircle2, AlertCircle, 
  MessageSquare, Phone, HelpCircle, Smartphone
} from 'lucide-react';

const AdminQuickContact = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // States for WhatsApp & Call
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [whatsappPosition, setWhatsappPosition] = useState('bottom-right');
  const [whatsappOffsetY, setWhatsappOffsetY] = useState(20);

  const [phoneEnabled, setPhoneEnabled] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phonePosition, setPhonePosition] = useState('bottom-right');
  const [phoneOffsetY, setPhoneOffsetY] = useState(90); // default higher so it doesn't overlap WhatsApp

  useEffect(() => {
    fetchQuickContactSettings();
  }, []);

  const fetchQuickContactSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      
      if (res.data.quick_contact) {
        const qc = res.data.quick_contact;
        setWhatsappEnabled(!!qc.whatsapp_enabled);
        setWhatsappNumber(qc.whatsapp_number || '');
        setWhatsappMessage(qc.whatsapp_message || '');
        setWhatsappPosition(qc.whatsapp_position || 'bottom-right');
        setWhatsappOffsetY(qc.whatsapp_offset_y !== undefined ? Number(qc.whatsapp_offset_y) : 20);

        setPhoneEnabled(!!qc.phone_enabled);
        setPhoneNumber(qc.phone_number || '');
        setPhonePosition(qc.phone_position || 'bottom-right');
        setPhoneOffsetY(qc.phone_offset_y !== undefined ? Number(qc.phone_offset_y) : 90);
      }
    } catch (err) {
      console.error("Load settings failed", err);
      setError("Ayarlar yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    const settingsData = {
      whatsapp_enabled: whatsappEnabled,
      whatsapp_number: whatsappNumber.trim(),
      whatsapp_message: whatsappMessage,
      whatsapp_position: whatsappPosition,
      whatsapp_offset_y: Number(whatsappOffsetY),
      phone_enabled: phoneEnabled,
      phone_number: phoneNumber.trim(),
      phone_position: phonePosition,
      phone_offset_y: Number(phoneOffsetY),
    };

    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: 'quick_contact',
        value_data: settingsData
      }, { withCredentials: true });
      
      setSuccess("Hızlı İletişim buton ayarları başarıyla kaydedildi!");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save settings failed", err);
      setError("Ayarlar kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-emerald-50/50">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-rose-50/50">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
          <Smartphone className="text-blue-600" size={24} />
          Hızlı İletişim Butonları (WhatsApp & Telefon)
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Web sitenizin alt köşelerinde yer alan, ziyaretçilerin tek tıkla doğrudan WhatsApp'tan yazmasını veya telefonla aramasını sağlayan butonları yapılandırın.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Form Controls (Left / 7 cols) */}
        <form onSubmit={handleSaveSettings} className="lg:col-span-7 space-y-6">
          
          {/* WHATSAPP SETTINGS */}
          <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                <MessageSquare className="text-emerald-500" size={18} />
                WhatsApp Destek Butonu
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={whatsappEnabled} 
                  onChange={(e) => setWhatsappEnabled(e.target.checked)} 
                  className="sr-only peer" 
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                <span className="ml-2 text-xs font-bold text-slate-600">{whatsappEnabled ? 'Aktif' : 'Pasif'}</span>
              </label>
            </div>

            {whatsappEnabled && (
              <div className="space-y-4 animate-fade-in">
                {/* Number */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    WhatsApp Numarası
                    <HelpCircle size={13} className="text-slate-400" title="Ülke kodu ile birlikte boşluk bırakmadan giriniz. Örn: +905320000000" />
                  </label>
                  <input
                    type="text"
                    required={whatsappEnabled}
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder="Örn: +905321234567"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                {/* Default Message */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    Hazır Mesaj Taslağı
                  </label>
                  <textarea
                    rows={2}
                    value={whatsappMessage}
                    onChange={(e) => setWhatsappMessage(e.target.value)}
                    placeholder="Ziyaretçi WhatsApp butonuna tıkladığında yazışma ekranında otomatik belirecek mesaj taslağı."
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Position & Offsets */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Yatay Hizalama</label>
                    <select
                      value={whatsappPosition}
                      onChange={(e) => setWhatsappPosition(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold bg-white focus:outline-none"
                    >
                      <option value="bottom-right">Sağ Alt Köşe</option>
                      <option value="bottom-left">Sol Alt Köşe</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      Dikey Yükseklik (Bottom Offset px)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={800}
                      value={whatsappOffsetY}
                      onChange={(e) => setWhatsappOffsetY(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* TELEPHONE CALL SETTINGS */}
          <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                <Phone className="text-blue-500" size={18} />
                Hızlı Arama (Telefon) Butonu
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={phoneEnabled} 
                  onChange={(e) => setPhoneEnabled(e.target.checked)} 
                  className="sr-only peer" 
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ml-2 text-xs font-bold text-slate-600">{phoneEnabled ? 'Aktif' : 'Pasif'}</span>
              </label>
            </div>

            {phoneEnabled && (
              <div className="space-y-4 animate-fade-in">
                {/* Number */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    Telefon Numarası
                  </label>
                  <input
                    type="text"
                    required={phoneEnabled}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Örn: +902128778918"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                {/* Position & Offsets */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700">Yatay Hizalama</label>
                    <select
                      value={phonePosition}
                      onChange={(e) => setPhonePosition(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold bg-white focus:outline-none"
                    >
                      <option value="bottom-right">Sağ Alt Köşe</option>
                      <option value="bottom-left">Sol Alt Köşe</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      Dikey Yükseklik (Bottom Offset px)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={800}
                      value={phoneOffsetY}
                      onChange={(e) => setPhoneOffsetY(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-right">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
            >
              <Save size={16} />
              {loading ? "Kaydediliyor..." : "Buton Ayarlarını Kaydet"}
            </button>
          </div>
        </form>

        {/* Real-time Simulator (Right / 5 cols) */}
        <div className="lg:col-span-5 space-y-6 lg:border-l lg:border-slate-100 lg:pl-8">
          <div>
            <h3 className="text-sm font-extrabold text-slate-800">Canlı Görsel Önizleme</h3>
            <p className="text-[11px] text-slate-500 mt-1">Butonların konumu ve hizalanmasını cep telefonu simülatörü üzerinde anlık olarak takip edebilirsiniz.</p>
          </div>

          {/* Smartphone mockup */}
          <div className="bg-slate-950 w-full max-w-[280px] h-[500px] rounded-[40px] p-3 shadow-xl border-4 border-slate-800 mx-auto relative overflow-hidden flex flex-col justify-between">
            {/* Phone speaker/notch */}
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-slate-800 flex items-center justify-center">
              <div className="w-8 h-1 rounded-full bg-slate-900"></div>
            </div>

            {/* Screen inner */}
            <div className="w-full h-full bg-slate-100 rounded-[30px] overflow-hidden relative border border-slate-900 flex flex-col justify-end p-4">
              <div className="text-center pb-24 text-[10px] font-bold text-slate-400">Web Siteniz</div>

              {/* SIMULATOR FLOATING BUTTONS */}
              {whatsappEnabled && whatsappNumber && (
                <div 
                  className={`absolute z-30 transition-all duration-300 ${
                    whatsappPosition === 'bottom-right' ? 'right-4' : 'left-4'
                  }`}
                  style={{ bottom: `${(whatsappOffsetY / 500) * 100}%` }}
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/30 flex items-center justify-center text-white border border-emerald-400 hover:scale-105 active:scale-95 transition-transform duration-200 cursor-pointer">
                    <MessageSquare size={18} fill="white" className="text-emerald-500" />
                  </div>
                </div>
              )}

              {phoneEnabled && phoneNumber && (
                <div 
                  className={`absolute z-30 transition-all duration-300 ${
                    phonePosition === 'bottom-right' ? 'right-4' : 'left-4'
                  }`}
                  style={{ bottom: `${(phoneOffsetY / 500) * 100}%` }}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-600 shadow-md shadow-blue-600/30 flex items-center justify-center text-white border border-blue-500 hover:scale-105 active:scale-95 transition-transform duration-200 cursor-pointer">
                    <Phone size={18} fill="white" className="text-blue-600" />
                  </div>
                </div>
              )}

              {/* Fake phone navbar navigation bar at bottom */}
              <div className="w-20 h-1 rounded-full bg-slate-400 mx-auto mt-2 shrink-0"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuickContact;
